import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import { TextField } from 'mui-rff';
import { Form } from 'react-final-form';
import { DataGrid, GridActionsCellItem } from '@mui/x-data-grid';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Edit as EditIcon,
  Add as AddIcon,
  ReplayCircleFilled as ReplayCircleFilledIcon,
} from '@mui/icons-material';

import ConfirmationDialog from '@components/ConfirmationDialog';
import { breadcrumbsActions } from '@store/breadcrumbs';
import { showSuccessMessage, showErrorMessage } from '@store/dashboard';
import ProdConfirmationDialog from '@components/ProdConfirmationDialog';

import {
  getErrorConfigList,
  addErrorConfig,
  updateErrorConfig,
  deleteErrorConfig,
  invalidateCache,
  selectErrorConfigList,
  selectIsLoadingErrorConfigList,
} from '../store/error-config';

import ApplicationPageTemplate from '../../dashboard/components/ApplicationPageTemplate/ApplicationPageTemplate';
import TableToolbar from '../../../components/TableToolbar';

import { viewErrorConfigs } from '../payment-configuration.breadcrumbs';

const MESSAGES = {
  // Page
  PageTitle: 'Error Config',

  // Dialogs
  DeleteConfirmationTitle: 'Delete Error Config',
  DeleteConfirmationMessage:
    'Are you sure you want to delete the selected error config?',
  RefreshCacheConfirmationTitle: 'Refresh Cache',
  RefreshCacheConfirmationMessage: 'Are you sure you want refresh the cache?',
  UpdateDialogTitle: 'Update Error Config',
  CreateDialogTitle: 'Add New Error Config',

  // Buttons
  RefreshCacheButton: 'Refresh Cache',
  CreateButton: 'Add New',
  EditButton: 'Edit',
  DeleteButton: 'Delete',
  SubmitButton: 'Submit',
  CancelButton: 'Cancel',

  // Fields
  IdFieldLabel: 'ID',
  ErrorCodeFieldLabel: 'Error Code',
  ErrorTypeFieldLabel: 'Error Type',
  GatewayErrorFieldLabel: 'Gateway Error',
  DefaultMessageFieldLabel: 'Default Message',
  ActionsFieldLabel: 'Actions',
  RequiredField: 'Required field',
  NumberField: 'Invalid value',

  // API Actions
  CreateSuccess: 'Yay! Error config has been created successfully.',
  CreateFailure: 'Oops! Something went wrong while creating the error config.',
  UpdateSuccess: 'Yay! Error config has been updated successfully.',
  UpdateFailure: 'Oops! Something went wrong while updating the error config.',
  DeleteSuccess: 'Yay! Error config has been deleted successfully.',
  DeleteFailure: 'Oops! Something went wrong while deleting the error config.',
  CacheRefreshSuccess: 'Yay! Cache refresh has been done successfully.',
  CacheRefreshFailure: 'Oops! Something went wrong while refreshing the cache.',
};

const FULFILLED_REQUEST_STATUS = 'fulfilled';

function ConfigToolbar({ onCreate, onRefreshCache, permissions = {} }) {
  return (
    <TableToolbar>
      <div>
        <Button
          variant="outlined"
          disabled={!permissions?.canRefreshErrorConfigCache}
          onClick={onRefreshCache}
          startIcon={<ReplayCircleFilledIcon />}
          style={{ marginRight: 10 }}>
          {MESSAGES.RefreshCacheButton}
        </Button>
        <Button
          disabled={!permissions?.canCreateErrorConfig}
          variant="contained"
          onClick={onCreate}
          startIcon={<AddIcon />}>
          {MESSAGES.CreateButton}
        </Button>
      </div>
    </TableToolbar>
  );
}

export default function ErrorConfig({ permissions = {} }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [openDialog, setOpenDialog] = useState({ show: false });

  const isLoadingErrorConfigList = useSelector(selectIsLoadingErrorConfigList);
  const errorConfigList = useSelector(selectErrorConfigList);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState({
    show: false,
  });
  const [showRefreshCacheConfirmation, setShowRefreshCacheConfirmation] =
    useState({ show: false });
  const [toDeleteConfig, setToDeleteConfig] = useState('');
  const [showFormDialog, setShowFormDialog] = useState(false);
  const [formValues, setFormValues] = useState({});
  const [isEditMode, setIsEditMode] = useState(false);

  // Check permissions
  useEffect(() => {
    if (!permissions?.canViewErrorConfig) {
      navigate('/landing');
    }
  }, [navigate, permissions]);

  // Breadcrumb
  useEffect(() => {
    dispatch(breadcrumbsActions.showMenu(true));
    dispatch(breadcrumbsActions.setBreadcrumbs(viewErrorConfigs));
  }, [dispatch, navigate]);

  // Get list data
  useEffect(() => {
    dispatch(getErrorConfigList());
  }, [dispatch]);

  const resetForm = useCallback(() => {
    setShowFormDialog(false);
    setFormValues({});
    setIsEditMode(false);
  }, []);

  const onCreate = () => {
    setShowFormDialog(true);
  };

  const onEdit = (row) => {
    setIsEditMode(true);
    setFormValues(row);
    setShowFormDialog(true);
  };

  const onDelete = ({ id }) => {
    setToDeleteConfig(id);
    setShowDeleteConfirmation({ show: true });
  };

  const onDeleteConfirmation = (id) => {
    dispatch(deleteErrorConfig({ id })).then(
      ({ meta: { requestStatus }, payload }) => {
        if (requestStatus !== FULFILLED_REQUEST_STATUS) {
          const message = payload?.message || MESSAGES.DeleteFailure;
          dispatch(showErrorMessage({ message, duration: 4000 }));
          return;
        }

        dispatch(showSuccessMessage(MESSAGES.DeleteSuccess));
        dispatch(getErrorConfigList());
      },
    );
  };

  const onRefreshCache = () => {
    setShowRefreshCacheConfirmation({ show: true });
  };

  const onRefreshCacheConfirmation = () => {
    dispatch(invalidateCache()).then(({ meta: { requestStatus }, payload }) => {
      if (requestStatus !== FULFILLED_REQUEST_STATUS) {
        const message = payload?.message || MESSAGES.CacheRefreshFailure;
        dispatch(showErrorMessage({ message, duration: 4000 }));
        return;
      }

      dispatch(showSuccessMessage(MESSAGES.CacheRefreshSuccess));
    });
  };

  const onSubmit = (payload) => {
    let action = addErrorConfig({ payload });
    let successMsg = MESSAGES.CreateSuccess;
    let failureMsg = MESSAGES.CreateFailure;

    if (isEditMode) {
      action = updateErrorConfig({ payload, id: payload.id });
      successMsg = MESSAGES.UpdateSuccess;
      failureMsg = MESSAGES.UpdateFailure;
    }

    dispatch(action).then(({ payload: response }) => {
      // On Failure
      if (!response || !response.data) {
        const message = response?.message || failureMsg;
        return dispatch(showErrorMessage({ message, duration: 5000 }));
      }

      // On Success
      dispatch(showSuccessMessage(successMsg));
      dispatch(getErrorConfigList());

      return resetForm();
    });
  };

  const formValidator = ({
    errorCode,
    errorType,
    gatewayError,
    defaultMessage,
  }) => ({
    ...((errorCode == null || Number.isNaN(errorCode)) && {
      errorCode: MESSAGES.RequiredField,
    }), // null, undefined or invalid number check for errorCode
    ...(errorCode < 0 && {
      errorCode: MESSAGES.NumberField,
    }), // positive number check for errorCode
    ...(!errorType && { errorType: MESSAGES.RequiredField }),
    ...(!gatewayError && { gatewayError: MESSAGES.RequiredField }),
    ...(!defaultMessage && { defaultMessage: MESSAGES.RequiredField }),
  });

  const handlePreSubmit = (e) => {
    e.preventDefault();
    setOpenDialog({ show: true }); // open the confirmation dialog if prod
  };

  return (
    <ApplicationPageTemplate
      title={MESSAGES.PageTitle}
      pageReady={!isLoadingErrorConfigList}>
      <DataGrid
        density="compact"
        rows={errorConfigList}
        disableColumnFilter
        disableColumnMenu
        disableColumnSelector
        disableDensitySelector
        disableSelectionOnClick
        components={{
          Toolbar: () =>
            ConfigToolbar({
              onCreate,
              onRefreshCache,
              permissions,
            }),
        }}
        componentsProps={{
          toolbar: {
            csvOptions: { disableToolbarButton: true },
            printOptions: { disableToolbarButton: true },
            showQuickFilter: true,
            quickFilterProps: { debounceMs: 500 },
          },
        }}
        columns={[
          {
            field: 'id',
            headerName: MESSAGES.IdFieldLabel,
            width: 100,
            sortable: false,
            headerAlign: 'center',
            align: 'center',
          },
          {
            field: 'errorCode',
            headerName: MESSAGES.ErrorCodeFieldLabel,
            sortable: false,
            headerAlign: 'center',
            align: 'center',
          },
          {
            field: 'errorType',
            headerName: MESSAGES.ErrorTypeFieldLabel,
            sortable: false,
            headerAlign: 'center',
            align: 'center',
          },
          {
            field: 'gatewayError',
            headerName: MESSAGES.GatewayErrorFieldLabel,
            sortable: false,
            headerAlign: 'left',
            align: 'left',
            flex: 2,
          },
          {
            field: 'defaultMessage',
            headerName: MESSAGES.DefaultMessageFieldLabel,
            sortable: false,
            headerAlign: 'left',
            align: 'left',
            flex: 2,
          },
          {
            field: 'actions',
            headerName: MESSAGES.ActionsFieldLabel,
            type: 'actions',
            headerAlign: 'center',
            align: 'center',
            flex: 1,
            getActions: ({ row }) => [
              <GridActionsCellItem
                icon={<EditIcon />}
                label={MESSAGES.EditButton}
                key="0"
                disabled={!permissions?.canDeleteErrorConfig}
                onClick={() => onEdit(row)}
              />,
              <GridActionsCellItem
                icon={<DeleteIcon />}
                label={MESSAGES.DeleteButton}
                key="1"
                disabled={!permissions?.canDeleteErrorConfig}
                onClick={() => onDelete(row)}
              />,
            ],
          },
        ]}
      />
      <ConfirmationDialog
        openDialog={showDeleteConfirmation}
        yesHandler={onDeleteConfirmation}
        data={toDeleteConfig}
        title={MESSAGES.DeleteConfirmationTitle}
        message={MESSAGES.DeleteConfirmationMessage}
      />
      <ConfirmationDialog
        openDialog={showRefreshCacheConfirmation}
        yesHandler={onRefreshCacheConfirmation}
        title={MESSAGES.RefreshCacheConfirmationTitle}
        message={MESSAGES.RefreshCacheConfirmationMessage}
      />
      <Dialog open={showFormDialog}>
        <DialogTitle>
          {isEditMode ? MESSAGES.UpdateDialogTitle : MESSAGES.CreateDialogTitle}
        </DialogTitle>
        <Form
          onSubmit={onSubmit}
          initialValues={isEditMode ? formValues : {}}
          validate={formValidator}
          render={({ submitError, handleSubmit, submitting, invalid }) => (
            <form
              onSubmit={(e) =>
                process.env.REACT_APP_ENV !== 'production'
                  ? handleSubmit(e)
                  : handlePreSubmit(e)
              }
              noValidate>
              <ProdConfirmationDialog
                openDialog={openDialog}
                yesHandler={handleSubmit}
              />
              <DialogContent style={{ minHeight: '185px' }}>
                <Box sx={{ marginTop: '10px', minHeight: '80px' }}>
                  <TextField
                    label={MESSAGES.ErrorCodeFieldLabel}
                    name="errorCode"
                    type="number"
                    required
                    variant="outlined"
                    fullWidth
                    disabled={isEditMode}
                    InputProps={{
                      readOnly: !permissions?.canUpdateErrorConfig,
                    }}
                  />
                  <TextField
                    label={MESSAGES.ErrorTypeFieldLabel}
                    name="errorType"
                    required
                    variant="outlined"
                    fullWidth
                    style={{ marginTop: 30 }}
                    InputProps={{
                      readOnly: !permissions?.canUpdateErrorConfig,
                    }}
                  />
                  <TextField
                    label={MESSAGES.GatewayErrorFieldLabel}
                    name="gatewayError"
                    required
                    variant="outlined"
                    fullWidth
                    disabled={isEditMode}
                    style={{ marginTop: 30 }}
                    InputProps={{
                      readOnly: !permissions?.canUpdateErrorConfig,
                    }}
                  />
                  <TextField
                    label={MESSAGES.DefaultMessageFieldLabel}
                    name="defaultMessage"
                    required
                    variant="outlined"
                    fullWidth
                    style={{ marginTop: 30 }}
                    InputProps={{
                      readOnly: !permissions?.canUpdateErrorConfig,
                    }}
                  />
                </Box>
                {submitError && (
                  <Alert
                    severity="error"
                    style={{ marginTop: 20 }}>
                    {submitError}
                  </Alert>
                )}
              </DialogContent>
              <DialogActions>
                <Button
                  variant="outlined"
                  onClick={resetForm}>
                  {MESSAGES.CancelButton}
                </Button>
                <Button
                  variant="contained"
                  type="submit"
                  disabled={
                    !permissions?.canUpdateErrorConfig ||
                    !permissions?.canCreateErrorConfig ||
                    submitting ||
                    invalid
                  }>
                  {MESSAGES.SubmitButton}
                </Button>
              </DialogActions>
            </form>
          )}
        />
      </Dialog>
    </ApplicationPageTemplate>
  );
}
